import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { jwtDecode } from 'jwt-decode';
import { catchError, exhaustMap, map, of, tap } from 'rxjs';

import * as AuthActions from './auth.actions';
import { AuthService } from 'projects/petszel-shared/src/lib/services/auth.service';
import { SharedLibraryService } from 'projects/petszel-shared/src/lib/services/shared.config';
import { NGXLogger } from "ngx-logger";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";

type DecodedToken = {
  'petszel-credentialtype': string | string[];
  [k: string]: any;
};

type AuthResponse = { isSuccess: boolean; token: string; expiration: string };

const TOKEN_KEY = 'token';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private router: Router,
    private http: HttpClient,
    private sharedLibraryService: SharedLibraryService,
    private legacyAuthService: AuthService,
    private logger: NGXLogger,
    private modalService: NgbModal
  ) {}

  loadFromStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadAuthFromStorage),
      map(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        this.logger.log('EFFECT Load From Storage', token);
        if (!token) {
          this.legacyAuthService.setLogInStatus(false);
          return AuthActions.hydrateFailure();
        }

        try {
          const decoded = jwtDecode<DecodedToken>(token);
          const cred = decoded['petszel-credentialtype'];
          const roles = Array.isArray(cred) ? cred : [cred];

          const ok = roles.includes('PetszelConnectClient');

          this.logger.log('EFFECT Load From Storage Try', decoded, cred, roles, ok);

          this.legacyAuthService.setLogInStatus(ok);

          return ok
            ? AuthActions.hydrateSuccess({ token, roles })
            : AuthActions.hydrateFailure();
        } catch (error: any)  {
          this.logger.log('EFFECT Load From Storage Catch', error);
          this.legacyAuthService.setLogInStatus(false);
          return AuthActions.hydrateFailure();
        }
      })
    )
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      exhaustMap(({ username, password }) => {
        const { apiUrl, applicationId } = this.sharedLibraryService.getSharedLibraryConfig() as any;
        const url = `${apiUrl}Authorize`;

        return this.http.post<AuthResponse>(url, { applicationId, username, password }).pipe(
          map((resp) => {
            if (!resp?.isSuccess || !resp.token || new Date(resp.expiration) <= new Date()) {
              throw new Error('Bad credentials');
            }

            const decoded = jwtDecode<DecodedToken>(resp.token);
            const cred = decoded['petszel-credentialtype'];
            const roles = Array.isArray(cred) ? cred : [cred];

            // OWNER ONLY
            if (!roles.includes('PetszelConnectClient')) {
              throw new Error('Not a pet parent account');
            }

            // persist + legacy status
            localStorage.setItem(TOKEN_KEY, resp.token);
            this.legacyAuthService.setLogInStatus(true);

            return AuthActions.hydrateSuccess({ token: resp.token, roles });
          }),
          tap(() => {
            // redirect behavior: honor ?redirect= first, else savedTargetUrl, else dashboard
            const tree = this.router.parseUrl(this.router.url);
            const redirect = tree.queryParams['redirect'] as string | undefined;
            const saved = localStorage.getItem('savedTargetUrl') ?? undefined;

            const target = redirect ?? saved ?? '/owner-dashboard';
            localStorage.removeItem('savedTargetUrl');

            this.router.navigateByUrl(target);
          }),
          catchError((e) => {
            localStorage.removeItem(TOKEN_KEY);
            this.legacyAuthService.setLogInStatus(false);

            const msg =
              e?.message === 'Not a pet parent account'
                ? 'This login is for Pet Parents only.'
                : 'Incorrect username or password.';

            return of(AuthActions.loginFailure({ error: msg }));
          })
        );
      })
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout, AuthActions.unauthorized),
      tap(() => {
        localStorage.removeItem(TOKEN_KEY);
        this.legacyAuthService.setLogInStatus(false);
        this.modalService.dismissAll();
        this.router.navigateByUrl('/');
      }),
      map(() => AuthActions.logoutSuccess())
    )
  );
}
